import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useMediaQuery } from "context/media/MediaContext";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { PasswordRefreshPersonelInfoAsync } from "redux/actions/password/Password.action";
import { forwardStep, resetStep } from "redux/slices/StepSlice";

import { yupResolver } from "@hookform/resolvers/yup";
import { passRefPersonelInfoSubmitValidationSchema } from "validations/password.validation";

import { INewPasswordPersonelInfoRequest } from "models/Password/password.model";
import { DIGIPOSTHEME } from "theme/digiPosTheme";
import {
  Flex,
  Panel,
  PanelHeader,
  TextField,
  Button,
  Spinner,
} from "components";
import Captcha from "components/BaseComponents/Captcha";
import Alert from "components/BaseComponents/Alert/Alert";
import { resetPassRefInfo } from "redux/slices/password-slices/PasswordRefreshSlice";
import { PasswordRefresh_refreshCaptcha } from "services/password/password.service";
import { ResetLogin } from "redux/slices/LoginSlice";
import { resetOtp } from "redux/slices/OtpSlice";
import { webSecurityAsync } from "redux/actions/WebSecurity.action";
import useLoading from "helpers/hooks/useLoading";
import InfoWarning from "./InfoWarning";

type PersonelInfoProps = {
  first: boolean;
};
const NewPassPersonelInfo: FC<PersonelInfoProps> = ({ first }) => {
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const {
    passwordInfo,
    success,
    isCaptcha,
    error,
    errorAlert,
  } = useAppSelector((state) => state.PassRefPersonelInfo);
  const [captchaToogle, setCaptchaToogle] = useState<boolean>(false);
  const [captchaVal, setCaptchaVal] = useState<string>();
  const [captchaError, setCaptchaError] = useState<string>("");
  const [captchaRequestCount, setCaptchaRequestCount] = useState<number>(0);
  const [isAddSuccess, setIsAddSuccess] = useState<boolean>(false);
  const [isAddError, setIsAddError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  const [firstVisible, setFirstVisible] = useState<boolean>(first);

  const requestData: INewPasswordPersonelInfoRequest = {
    phoneNumber: "",
    merchantNo: "",
    // MBB: "",
    // CCLastFourDigit: "",
    // Captcha: "",
  };
  const {
    handleSubmit,
    control,
    errors,
    reset,
    setValue,
  } = useForm<INewPasswordPersonelInfoRequest>({
    defaultValues: requestData,
    resolver: yupResolver(passRefPersonelInfoSubmitValidationSchema(t)),
  });
  const onSubmit = async (
    data: INewPasswordPersonelInfoRequest
  ): Promise<void> => {
    setIsLoading(true);
    setCaptchaToogle(false);
    dispatch(PasswordRefreshPersonelInfoAsync(data)); //FRM action , service ve slice duzenle / degistir
    dispatch(resetPassRefInfo());
    reset();
  };

  const handleMbbchange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(webSecurityAsync());
  };

  // const handleRefreshClick = async (): Promise<void> => {
  //   const retVal = await PasswordRefresh_refreshCaptcha();
  //   if (retVal.CaptchaRequestCount > 0) {
  //     setCaptchaVal(retVal.Captcha);
  //     setCaptchaRequestCount(retVal.CaptchaRequestCount);
  //   } else {
  //     setIsAddError(true);
  //     return;
  //   }
  // };

  useEffect(() => {
    if (success) {
      setIsLoading(false);
      dispatch(forwardStep(1));
    } else if (isCaptcha) {
      setIsLoading(false);
      setCaptchaVal(passwordInfo?.Captcha);
      setCaptchaToogle(true);
      setIsAddError(true);
      const m = passwordInfo?.DialogBoxes[0]?.Message;
      setCaptchaError(m);
    }
  }, [success, isCaptcha, dispatch]);

  useEffect(() => {
    if (firstVisible) {
      setCaptchaToogle(false);
      setCaptchaVal("");
      setIsAddError(false);
      setIsLoading(false);
      // setValue("Captcha", "");
      dispatch(resetPassRefInfo());

      setFirstVisible(false);
    }
    if (error && !isCaptcha) {
      setCaptchaToogle(false);
      setCaptchaVal("");
      setIsAddError(true);
      setIsLoading(false);
      // setValue("Captcha", "");
    }
  }, [error, firstVisible]);

  useEffect(() => {
    if (errorAlert) {
      setIsAddError(true);
      reset();
    }
  }, [errorAlert]);

  const StepExit = () => {
    dispatch(resetStep());
    dispatch(ResetLogin());
    dispatch(resetOtp());
    navigate(-1);
  };

  return (
    <>
      {isAddError && (
        <Alert
          alertType='Error'
          duration={6000}
          isVisible={captchaToogle || !!errorAlert}
          message={captchaToogle ? captchaError : errorAlert}
          setIsAddSuccess={setIsAddSuccess}
          setIsAddError={setIsAddError}
        />
      )}
      <Spinner show={isLoading} />
      <Panel width='646px' marginTop='0'>
        <PanelHeader title={t("change_password_check_user")} />
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Flex.StyledFormWrapper>
            <Flex.Row>
              <Flex.Col>
                <TextField
                  control={control}
                  error={errors.phoneNumber?.message}
                  label={t("phone_number") || ""}
                  maxLength={15}
                  name='phoneNumber'
                  type='phoneNumber'
                  placeholder='(5**) *** ** **' //{t("phone_number_required_validation") || ""}
                  onBlur={handleMbbchange}
                />
              </Flex.Col>
            </Flex.Row>
            <Flex.Row>
              <Flex.Col>
                <TextField
                  control={control}
                  error={errors.merchantNo?.message}
                  label={t("merchant_number") || ""}
                  maxLength={10}
                  name='merchantNo'
                  type='numeric'
                  placeholder={t("merchant_number_placeholder") || ""}
                />
              </Flex.Col>
            </Flex.Row>

            {/* {captchaToogle && (
              <>
                <Flex.Row>
                  <Flex.Col margin='0 10px 0 0'>
                    <TextField
                      control={control}
                      error={errors.Captcha?.message}
                      label={t("security_code").toString()}
                      maxLength={4}
                      name='Captcha'
                      type='alphaNumeric'
                    />
                  </Flex.Col>

                  <Flex.Col>
                    <Captcha
                      imgSrc={captchaToogle ? `${captchaVal}` : undefined}
                      isRefreshable={captchaRequestCount === 3 ? false : true}
                      onRefreshClick={handleRefreshClick}
                    />
                  </Flex.Col>
                </Flex.Row>
              </>
            )} */}
          </Flex.StyledFormWrapper>

          <Flex.Row
            backgroundColor={DIGIPOSTHEME.colors.secondaryOffWhite}
            justifyContent='flex-end'
            isResponsive
            noMargin
            colReverse
          >
            <Flex.Col>
              <Button
                type='button'
                width={isMobile ? "100%" : "100px"}
                text={`← ${t("back")}`}
                onClick={() => StepExit()}
                color='light'
              />
            </Flex.Col>
            <Flex.Col offSet={22} size={0.5}>
              <Button text={t("continue").toString()} type='submit' />
            </Flex.Col>
          </Flex.Row>
        </form>

        <Flex.Row
          backgroundColor={DIGIPOSTHEME.colors.secondaryWhite}
          padding='20px 0 0 0'
        >
          <InfoWarning />
        </Flex.Row>
      </Panel>
    </>
  );
};

export default NewPassPersonelInfo;
